import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotEventContainerCudLog} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {
    AMARON_JOURNEY_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/amaron";
import type {ActivityTimelineResolverContext} from "~/modules/activityTimeline/types/activityTimeline.types";
import {resolveNotesFieldChange} from "~/modules/activityTimeline/utils/activityTimelineNotesUtils";

const AMARON_DISPOSITION_COLUMN_IDS = new Set(
    [
        AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
        AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    ].map(String),
);

const AMARON_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS = new Set(
    AMARON_JOURNEY_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS.map(String),
);

export type EventContainerFieldChange = {
    fieldKey: string;
    fieldLabel: string;
    oldValue: unknown;
    newValue: unknown;
};

function valuesEqual(left: unknown, right: unknown): boolean {
    if (left === right) {
        return true;
    }
    if (left === null || left === undefined || left === "") {
        return right === null || right === undefined || right === "";
    }
    if (typeof left === "object" || typeof right === "object") {
        try {
            return JSON.stringify(left) === JSON.stringify(right);
        } catch {
            return false;
        }
    }
    return String(left) === String(right);
}

function resolveColumnLabel(getAdditionalColumns: CapabilityUdoColumnResolver,
    columnId: string,
    context: ActivityTimelineResolverContext,
): string {
    return getAdditionalColumns(context.eventContainerTemplate).find(
        (column) => String(column.id) === columnId,
    )?.displayName ?? columnId;
}

function shouldSkipColumn(
    columnId: string,
    context: ActivityTimelineResolverContext,
): boolean {
    if (String(context.eventContainerTemplate.configuration.dispositionColumnConfig.columnId) === columnId) {
        return true;
    }
    if (
        context.workspaceSpecific?.remarksColumnId !== null &&
        context.workspaceSpecific?.remarksColumnId !== undefined &&
        String(context.workspaceSpecific.remarksColumnId) === columnId
    ) {
        return true;
    }
    if (context.workspaceSpecific?.tryAmaronLevelColumns === true) {
        return (
            AMARON_DISPOSITION_COLUMN_IDS.has(columnId) ||
            AMARON_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS.has(columnId)
        );
    }
    return false;
}

export function extractEventContainerSnapshotFieldChanges(getAdditionalColumns: CapabilityUdoColumnResolver,
    previousSnapshot: OttopilotEventContainerCudLog,
    currentSnapshot: OttopilotEventContainerCudLog,
    context: ActivityTimelineResolverContext,
): Array<EventContainerFieldChange> {
    const changes: Array<EventContainerFieldChange> = [];
    const notesChange = resolveNotesFieldChange(previousSnapshot.notes, currentSnapshot.notes);
    if (notesChange !== null) {
        changes.push({
            fieldKey: "notes",
            fieldLabel: "Notes",
            oldValue: notesChange.oldValue,
            newValue: notesChange.newValue,
        });
    }

    const additionalColumnIds = new Set([
        ...Object.keys(previousSnapshot.additionalColumnValues),
        ...Object.keys(currentSnapshot.additionalColumnValues),
    ]);
    for (const columnId of additionalColumnIds) {
        if (shouldSkipColumn(columnId, context)) {
            continue;
        }
        const oldValue = previousSnapshot.additionalColumnValues[columnId as Uuid];
        const newValue = currentSnapshot.additionalColumnValues[columnId as Uuid];
        if (valuesEqual(oldValue, newValue)) {
            continue;
        }
        changes.push({
            fieldKey: columnId,
            fieldLabel: resolveColumnLabel(getAdditionalColumns, columnId, context),
            oldValue,
            newValue,
        });
    }

    return changes;
}
